import { Box, Typography, Card, CardContent, Stack, Button, Divider, Chip, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Timeline as TimelineIcon,
  Code as CodeIcon,
  Tune as TuneIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Webhook as WebhookIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

export default function ChangeDetectionIndex() {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Change History',
      description: 'View all detected changes across your pages with filters, badges, and webhook status',
      icon: <TimelineIcon fontSize="large" color="primary" />,
      path: '/analytics/change-detection',
      color: '#1976d2',
    },
    {
      title: 'HTML Diff Tool',
      description: 'Manually compare two HTML snippets side-by-side with visual difference highlighting',
      icon: <CodeIcon fontSize="large" color="success" />,
      path: '/analytics/html-diff',
      color: '#2e7d32',
    },
    {
      title: 'Configuration',
      description: 'Configure webhooks, change type filters, detection frequency, and notification settings',
      icon: <TuneIcon fontSize="large" color="warning" />,
      path: '/analytics/change-detection/config',
      color: '#ed6c02',
    },
    {
      title: 'Page SEO Validation',
      description: 'On-demand validation of individual pages for SEO compliance and technical issues',
      icon: <CheckCircleIcon fontSize="large" color="secondary" />,
      path: '/validation/history',
      color: '#9c27b0',
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack spacing={2} mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Change Detection
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Automated monitoring and detection of SEO-critical changes across your website pages
        </Typography>
      </Stack>

      {/* Overview Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            What is Change Detection?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Change Detection automatically monitors your web pages for SEO-critical changes including meta tags, 
            canonical URLs, indexability settings, content modifications, and schema markup. When changes are detected, 
            the system logs them, sends webhooks (if configured), and provides detailed history for analysis.
          </Typography>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            How Does It Work?
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <ScheduleIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      1. Scheduled Batch Processing
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Runs daily at <strong>4:00 AM UTC</strong> via APScheduler. 
                      Gets list of pages from experiment tracking, crawls them, and detects changes.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <StorageIcon color="success" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      2. Data Collection Flow (Multiple Sources)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>A) Scheduled Batch Crawling:</strong><br />
                      • Page Snapshot Job (3 AM UTC): Crawls ALL active pages in 'pages' table using httpx GET requests<br />
                      • Stores HTML snapshots → analytics_page_snapshots table<br />
                      • Change Detection Job (4 AM UTC): Compares latest 2 snapshots<br />
                      <br />
                      <strong>B) Ad-Hoc Validation Snapshots:</strong><br />
                      • Page SEO Validation: User triggers on-demand validation<br />
                      • Core API ScheduledChangeDetectionService (hourly): Processes validation snapshots<br />
                      • Writes to page_change_events (Core API table) + analytics_change_history (Analytics Service)<br />
                      <br />
                      <em>Both sources feed change detection - batch crawl for all pages, ad-hoc for user-requested pages.</em>
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <WebhookIcon color="warning" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      3. Dual Storage Architecture
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • <strong>Core API</strong>: page_change_events (internal experiment correlation)<br />
                      • <strong>Analytics Service</strong>: analytics_change_history (customer-facing API)<br />
                      <br />
                      Both tables populated simultaneously by batch job. Different schemas for different use cases.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckCircleIcon color="secondary" />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      4. Post-Detection Actions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Sends webhook notifications (if configured)<br />
                      • Applies SHA-256 deduplication (7-day window)<br />
                      • Logs response codes and timestamps<br />
                      <br />
                      <em>Note: Page SEO Validation is separate - manual on-demand validation tool.</em>
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monitored Change Types */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Monitored Change Types
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="🏷️ Meta Tags" color="info" />
            <Chip label="🔗 Canonical URLs" color="warning" />
            <Chip label="🚫 Indexability" color="error" />
            <Chip label="📄 Content Changes" color="success" />
            <Chip label="🎨 Template Changes" color="default" />
            <Chip label="📐 Schema Markup" color="secondary" />
          </Stack>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Available Tools
      </Typography>
      <Grid container spacing={2}>
        {features.map((feature) => (
          <Grid item xs={12} sm={6} md={3} key={feature.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
                cursor: 'pointer',
              }}
              onClick={() => navigate(feature.path)}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: `${feature.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(feature.path)}
                    fullWidth
                  >
                    Open
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
